<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import i18n from "@/i18n";
import { SHARED } from "@/constants";
import { mapState } from "vuex";
import { LEAD_MODULE } from "@/state/modules/lead";
import { leadMethods, usersMethods, clientMethods, crmOptionMethods } from "@/state/helpers";
import { CLIENT_MODULE } from "@/state/modules/client";
import { CRM_OPTION_MODULE } from "@/state/modules/crmOption";
import { sharedHelper } from "@/utils";
import Multiselect from "vue-multiselect";
import "vue-multiselect/dist/vue-multiselect.min.css";

const STATUS_VARIANTS = {
  new: "info",
  contacted: "warning",
  qualified: "primary",
  converted: "success",
  lost: "secondary",
};

export default {
  page: { title: i18n.t("lead.title") },
  components: { Layout, PageHeader, Multiselect },
  data() {
    return {
      title: i18n.t("lead.title"),
      currentPage: 1,
      perPage: SHARED.DEFAULT_PAGE_SIZE || 15,
      search: "",
      statusFilter: null,
      sourceFilter: null,
      loading: true,
      fields: [
        { key: "company_name", label: i18n.t("lead.companyName") },
        { key: "contact_person", label: i18n.t("lead.contactPerson") },
        { key: "phone", label: i18n.t("lead.phone") },
        { key: "source", label: i18n.t("lead.source") },
        { key: "status", label: i18n.t("label.status") },
        { key: "assignee", label: i18n.t("lead.assignee") },
        { key: "actions", label: "" },
      ],
      // convert modal
      convertTarget: null,
      convertDebtor: null,
      convertMode: "link",
      newClient: {},
      converting: false,
      // assign modal
      assignTarget: null,
      assignUser: null,
    };
  },
  computed: {
    /** Rows on this page, for the "showing X of Y" line. */
    rowCount() { return (this.leads || []).length; },
    ...mapState(LEAD_MODULE, ["stats", "pagination"]),
    ...mapState(CLIENT_MODULE, { clientList: "list" }),
    leads() { return this.$store.state.lead.list || []; },
    // Vocabularies come from /v1/crm/options so a status added in
    // configurations appears in the filters without a frontend change.
    statusOptions() { return this.$store.getters[`${CRM_OPTION_MODULE}/group`]("lead_statuses").map((o) => o.value); },
    sourceOptions() { return this.$store.getters[`${CRM_OPTION_MODULE}/group`]("lead_sources").map((o) => o.value); },
    userOptions() {
      return (this.$store.state.users.list.data || []).map((u) => ({ uuid: u.id, name: u.name }));
    },
    debtorOptions() {
      const items = this.clientList?.data || [];
      return items.map((c) => ({
        account_no: c.accountNo || c.account_no,
        company_name: c.companyName || c.company_name,
      })).filter((c) => c.account_no);
    },
  },
  async created() {
    await Promise.all([this.fetch(), this.getLeadStats(), this.getCrmOptions()]);
  },
  methods: {
    ...leadMethods,
    ...usersMethods,
    ...clientMethods,
    ...crmOptionMethods,
    async fetch() {
      this.loading = true;
      await this.getAllLeads({
        filter: {
          page: this.currentPage,
          size: this.perPage,
          search: this.search || undefined,
          status: this.statusFilter || undefined,
          source: this.sourceFilter || undefined,
        },
      });
      this.loading = false;
    },
    applyFilters() {
      this.currentPage = 1;
      this.fetch();
    },
    pageNavigation(page) {
      this.currentPage = page;
      this.fetch();
    },
    statusVariant(s) { return STATUS_VARIANTS[s] || "light"; },
    statusColor(s) { return this.$store.getters[`${CRM_OPTION_MODULE}/colorOf`]("lead_statuses", s); },
    statusLabel(s) { return this.$store.getters[`${CRM_OPTION_MODULE}/labelOf`]("lead_statuses", s); },
    sourceLabel(s) { return s ? this.$store.getters[`${CRM_OPTION_MODULE}/labelOf`]("lead_sources", s) : "—"; },
    goForm(uuid) {
      if (!this.$can("Update CRM")) return;
      this.$router.push({ name: "Lead-form-update", params: { uuid } });
    },
    goCreate() { this.$router.push({ name: "Lead-create" }); },
    goDetail(row) { this.$router.push({ name: "Lead-detail", params: { uuid: row.uuid } }); },
    goEdit(row) { this.$router.push({ name: "Lead-update", params: { uuid: row.uuid } }); },
    async removeLead(row) {
      if (!confirm(this.$t("lead.deleteConfirm", [row.company_name || row.contact_person]))) return;
      if (await this.deleteLead({ uuid: row.uuid })) {
        sharedHelper.alertSuccessMessage(this.$t("message.deleteXSuccess", [this.$t("lead.title")]));
        await Promise.all([this.fetch(), this.getLeadStats()]);
      }
    },
    async openConvert(row) {
      this.convertTarget = row;
      this.convertDebtor = null;
      // A lead with no company name is almost always an individual we already
      // deal with, so default to linking; one with a company is usually new.
      this.convertMode = row.company_name ? "create" : "link";
      this.newClient = {
        company_name: row.company_name || "",
        attention: row.contact_person || "",
        phone1: row.phone || "",
        email_address: row.email || "",
        address1: row.address || "",
        post_code: "",
        register_no: "",
      };
      if (!this.debtorOptions.length) {
        await this.getAllClients({ filter: { pageNumber: 1, pageSize: 1000 } });
      }
      this.$bvModal.show("convert-modal");
    },
    canConvert() {
      return this.convertMode === "link"
        ? !!this.convertDebtor
        : !!(this.newClient.company_name || "").trim();
    },
    async doConvert() {
      if (!this.canConvert() || this.converting) return;
      this.converting = true;

      try {
        const done = this.convertMode === "link"
          ? await this.linkToExisting()
          : await this.createAndConvert();
        if (!done) return;

        this.$bvModal.hide("convert-modal");
        await Promise.all([this.fetch(), this.getLeadStats()]);
      } finally {
        this.converting = false;
      }
    },
    async linkToExisting() {
      const r = await this.convertLead({
        uuid: this.convertTarget.uuid,
        debtorCode: this.convertDebtor.account_no,
      });
      if (r) sharedHelper.alertSuccessMessage(this.$t("lead.convertSuccess"));
      return !!r;
    },
    /**
     * Creates the customer and converts the lead in one API call — the backend
     * does both together so a created customer can never be left with the lead
     * still marked "new", which is how the same company gets opened twice.
     */
    async createAndConvert() {
      const data = { ...this.newClient, lead_uuid: this.convertTarget.uuid };
      Object.keys(data).forEach((k) => {
        if (data[k] === "" || data[k] === null) delete data[k];
      });

      const r = await this.createClient({ data });
      if (!r) return false;

      const accNo = r?.data?.account_no;
      sharedHelper.alertSuccessMessage(
        accNo ? this.$t("lead.convertCreatedX", [accNo]) : this.$t("lead.convertSuccess")
      );
      // The new customer is not in the cached dropdown yet.
      await this.getAllClients({ filter: { pageNumber: 1, pageSize: 1000 } });
      return true;
    },
    async openAssign(row) {
      this.assignTarget = row;
      this.assignUser = null;
      if (!this.userOptions.length) {
        await this.getAllUser({ filter: { pageNumber: 1, pageSize: 1000 } });
      }
      this.$bvModal.show("assign-modal");
    },
    async doAssign() {
      if (!this.assignUser) return;
      const r = await this.assignLead({ uuid: this.assignTarget.uuid, assignedTo: this.assignUser.uuid });
      if (r) {
        this.$bvModal.hide("assign-modal");
        sharedHelper.alertSuccessMessage(this.$t("lead.assignSuccess"));
        await this.fetch();
      }
    },
  },
};
</script>

<template>
  <Layout>
    <PageHeader :title="title" />

    <!-- Stats cards -->
    <div class="row">
      <div class="col-6 col-md" v-for="s in statusOptions" :key="s">
        <div class="card kpi" :style="{ borderTopColor: statusColor(s) }">
          <div class="card-body">
            <div class="kpi-label">{{ statusLabel(s) }}</div>
            <div class="kpi-value">{{ stats[s] || 0 }}</div>
          </div>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <!-- Filters -->
            <div class="row mb-3">
              <div class="col-12 col-md-4 mb-2 mb-md-0">
                <b-form-input
                  v-model="search"
                  type="search"
                  :placeholder="$t('lead.searchPlaceholder')"
                  @change="applyFilters"
                />
              </div>
              <div class="col-6 col-md-3">
                <multiselect
                  v-model="statusFilter"
                  :options="statusOptions"
                  :custom-label="statusLabel"
                  :show-labels="false"
                  :allow-empty="true"
                  open-direction="below"
                  :placeholder="$t('label.status')"
                  @input="applyFilters"
                />
              </div>
              <div class="col-6 col-md-3">
                <multiselect
                  v-model="sourceFilter"
                  :options="sourceOptions"
                  :custom-label="sourceLabel"
                  :show-labels="false"
                  :allow-empty="true"
                  open-direction="below"
                  :placeholder="$t('lead.source')"
                  @input="applyFilters"
                />
              </div>
              <div class="col-12 col-md-2 text-md-right mt-2 mt-md-0">
                <b-button v-if="$can('Create CRM')" variant="primary" size="sm" @click="goCreate">
                  <i class="mdi mdi-plus mr-1"></i>{{ $t('lead.create') }}
                </b-button>
              </div>
            </div>

            <div v-if="loading" class="text-center py-5">
              <b-spinner small variant="primary"></b-spinner>
            </div>

            <div v-else-if="!leads.length" class="text-center py-5">
              <i class="mdi mdi-account-search-outline display-4 text-muted"></i>
              <p class="text-muted mb-0">{{ $t('lead.noLeads') }}</p>
            </div>

            <div v-else class="table-responsive mb-0">
              <b-table :items="leads" :fields="fields" responsive="sm" small hover @row-clicked="goDetail">
                <template #cell(company_name)="data">
                  <strong>{{ data.item.company_name || '—' }}</strong>
                </template>
                <template #cell(contact_person)="data">{{ data.item.contact_person || '—' }}</template>
                <template #cell(phone)="data">{{ data.item.phone || '—' }}</template>
                <template #cell(source)="data">
                  <b-badge variant="light">{{ sourceLabel(data.item.source) }}</b-badge>
                  <!-- Captured by one of our own public forms: say which, and
                       link to it, because "website" alone does not tell you
                       which campaign is actually working. -->
                  <a
                    v-if="data.item.lead_form"
                    href="javascript:void(0);"
                    class="form-chip"
                    v-b-tooltip.hover
                    :title="$t('lead.fromForm', [data.item.lead_form.name])"
                    @click.stop="goForm(data.item.lead_form.uuid)"
                  >
                    <i class="mdi mdi-form-select mr-1"></i>{{ data.item.lead_form.name }}
                  </a>
                </template>
                <template #cell(status)="data">
                  <b-badge :variant="statusVariant(data.item.status)">{{ statusLabel(data.item.status) }}</b-badge>
                </template>
                <template #cell(assignee)="data">
                  {{ (data.item.assignee && data.item.assignee.name) || '—' }}
                </template>
                <template #cell(actions)="data">
                  <div class="text-nowrap" @click.stop>
                    <!-- Clicking the row already opens this, but an action column
                         that offers Edit and not View reads as if there is no
                         detail page at all. -->
                    <a
                      href="javascript:void(0);"
                      class="text-secondary mr-2"
                      v-b-tooltip.hover
                      :title="$t('label.view')"
                      @click="goDetail(data.item)"
                    ><i class="mdi mdi-eye-outline"></i></a>
                    <a
                      v-if="$can('Update CRM') && data.item.status !== 'converted'"
                      href="javascript:void(0);"
                      class="text-success mr-2"
                      v-b-tooltip.hover
                      :title="$t('lead.convert')"
                      @click="openConvert(data.item)"
                    ><i class="mdi mdi-account-convert"></i></a>
                    <a
                      v-if="$can('Update CRM')"
                      href="javascript:void(0);"
                      class="text-primary mr-2"
                      v-b-tooltip.hover
                      :title="$t('lead.assign')"
                      @click="openAssign(data.item)"
                    ><i class="mdi mdi-account-arrow-right"></i></a>
                    <a
                      v-if="$can('Update CRM')"
                      href="javascript:void(0);"
                      class="text-info mr-2"
                      v-b-tooltip.hover
                      :title="$t('label.updateX', [$t('module.lead')])"
                      @click="goEdit(data.item)"
                    ><i class="mdi mdi-pencil"></i></a>
                    <a
                      v-if="$can('Delete CRM')"
                      href="javascript:void(0);"
                      class="text-danger"
                      v-b-tooltip.hover
                      :title="$t('label.deleteX', [$t('module.lead')])"
                      @click="removeLead(data.item)"
                    ><i class="mdi mdi-delete"></i></a>
                  </div>
                </template>
              </b-table>
            </div>

            <!-- Always visible, unlike the pager: with fewer rows than a page
                 the pager hides and there is then nothing on screen saying
                 whether you are looking at everything or not. -->
            <div class="row mt-2" v-if="pagination.total">
              <div class="col text-muted small align-self-center">
                {{ $t('label.showingXofY', [rowCount, pagination.total]) }}
              </div>
              <!-- Shown even for a single page: hiding it made the list look
                   like it had no paging at all, and there was no way to tell a
                   short list from a truncated one. -->
              <div class="col">
                <div class="dataTables_paginate paging_simple_numbers float-right">
                  <ul class="pagination pagination-rounded mb-0">
                    <b-pagination
                      v-model="currentPage"
                      :total-rows="pagination.total"
                      :per-page="perPage"
                      @change="pageNavigation"
                    ></b-pagination>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Convert modal -->
    <b-modal id="convert-modal" :title="$t('lead.convert')" hide-footer>
      <p class="text-muted small">{{ $t('lead.convertHelp') }}</p>

      <b-form-radio-group v-model="convertMode" stacked class="mb-3">
        <b-form-radio value="link">{{ $t('lead.convertLinkExisting') }}</b-form-radio>
        <b-form-radio value="create">{{ $t('lead.convertCreateNew') }}</b-form-radio>
      </b-form-radio-group>

      <div v-if="convertMode === 'link'" class="row">
        <div class="col-12 mb-3">
          <multiselect
            v-model="convertDebtor"
            :options="debtorOptions"
            track-by="account_no"
            :custom-label="(c) => `${c.account_no} — ${c.company_name || ''}`"
            :show-labels="false"
            open-direction="below"
            :placeholder="$t('lead.selectDebtor')"
          />
        </div>
      </div>

      <!-- Prefilled from the lead: nobody should retype what we already know.
           Account number is generated by the backend, so it is not asked for. -->
      <div v-else class="row">
        <div class="col-12 mb-2">
          <label class="small">{{ $t('clientForm.companyName') }} <span class="text-danger">*</span></label>
          <b-form-input v-model="newClient.company_name" size="sm" />
        </div>
        <div class="col-12 mb-2">
          <label class="small">{{ $t('clientForm.attention') }}</label>
          <b-form-input v-model="newClient.attention" size="sm" />
        </div>
        <div class="col-12 mb-2">
          <label class="small">{{ $t('clientForm.phone1') }}</label>
          <b-form-input v-model="newClient.phone1" size="sm" />
        </div>
        <div class="col-12 mb-2">
          <label class="small">{{ $t('clientForm.email') }}</label>
          <b-form-input v-model="newClient.email_address" size="sm" />
        </div>
        <div class="col-12 mb-2">
          <label class="small">{{ $t('clientForm.address1') }}</label>
          <b-form-input v-model="newClient.address1" size="sm" />
        </div>
        <div class="col-12 mb-2">
          <label class="small">{{ $t('clientForm.postCode') }}</label>
          <b-form-input v-model="newClient.post_code" size="sm" />
        </div>
        <div class="col-12 mb-3">
          <label class="small">{{ $t('clientForm.registerNo') }}</label>
          <b-form-input v-model="newClient.register_no" size="sm" />
          <small class="text-muted">{{ $t('lead.convertAccNoAuto') }}</small>
        </div>
      </div>

      <div class="text-right">
        <b-button variant="light" class="mr-2" @click="$bvModal.hide('convert-modal')">{{ $t('label.cancel') }}</b-button>
        <b-button variant="primary" :disabled="!canConvert() || converting" @click="doConvert">
          <b-spinner v-if="converting" small class="mr-1"></b-spinner>
          {{ $t('lead.convert') }}
        </b-button>
      </div>
    </b-modal>

    <!-- Assign modal -->
    <b-modal id="assign-modal" :title="$t('lead.assign')" hide-footer>
      <div class="row">
        <div class="col-12 mb-3">
          <multiselect
            v-model="assignUser"
            :options="userOptions"
            track-by="uuid"
            label="name"
            :show-labels="false"
            open-direction="below"
            :placeholder="$t('lead.selectUser')"
          />
        </div>
      </div>
      <div class="text-right">
        <b-button variant="light" class="mr-2" @click="$bvModal.hide('assign-modal')">{{ $t('label.cancel') }}</b-button>
        <b-button variant="primary" :disabled="!assignUser" @click="doAssign">{{ $t('lead.assign') }}</b-button>
      </div>
    </b-modal>
  </Layout>
</template>

<style scoped>
.form-chip {
  display: inline-block;
  margin-top: 3px;
  padding: 1px 7px;
  border-radius: 10px;
  font-size: 11px;
  background: rgba(124, 77, 255, 0.1);
  color: #7c4dff;
  white-space: nowrap;
}
.form-chip:hover { background: rgba(124, 77, 255, 0.2); color: #6a3ce8; }
.kpi { border: 1px solid #e9ecef; }
.kpi .card-body { padding: 14px 16px; }
.kpi-label { font-size: 11px; color: #8993A4; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 4px; }
.kpi-value { font-size: 22px; font-weight: 700; color: #212529; }
.kpi { border-top: 3px solid #8993A4; }
</style>
